import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { LoaderService } from './loader.service';

@Injectable({ providedIn: 'root' })
export class BarcodeScannerService {

    private readonly SCAN_SPEED_MS = 80;     // max ms between chars for scanner (USB scanners: 1-5ms, fast human: ~50-80ms)
    private readonly MIN_SCAN_CONFIRM_CHARS = 5; // nb of consecutive fast chars required before declaring a scan sequence
    private readonly MIN_BARCODE_LENGTH = 3;
    private readonly AUTH_URLS = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email'];

    private buffer = '';
    private lastCharTime = 0;
    private fieldSnapshot: string | null = null;
    private fieldElement: HTMLInputElement | null = null;
    private isScanSequence = false;
    private _pendingBarcode: string | null = null;
    private scannedSearchElement: HTMLInputElement | null = null;
    private hidDevice: any = null;
    private hidBuffer = '';
    private hidFlushTimer: any = null;
    private lastEmittedBarcode = '';
    private lastEmitTime = 0;

    private readonly barcodeSubject = new Subject<string>();
    readonly scanned$: Observable<string> = this.barcodeSubject.asObservable();

    get hidConnected(): boolean { return this.hidDevice !== null; }

    constructor(
        private router: Router,
        private authService: AuthService,
        private ngZone: NgZone,
        private matDialog: MatDialog,
        private loaderService: LoaderService
    ) {}

    init(): void {
        window.addEventListener('keydown', (e) => this.onKeydown(e), true);
    }

    /** Consume the barcode stored during navigation (called by AddOrderComponent after products load). */
    consumePendingBarcode(): string | null {
        const b = this._pendingBarcode;
        this._pendingBarcode = null;
        return b;
    }

    /**
     * Connect to the barcode scanner via Web HID API (MI_01 vendor interface).
     * Returns true on success, false otherwise.
     * When connected, keyboard-based emission is suppressed (field protection still active).
     */
    async connectScanner(): Promise<boolean> {
        if (!('hid' in navigator)) return false;
        try {
            const devices = await (navigator as any).hid.requestDevice({
                filters: [{ vendorId: 0xFFFF, productId: 0x0035 }]
            });
            if (!devices.length) return false;

            this.hidDevice = devices[0];
            await this.hidDevice.open();

            this.hidDevice.addEventListener('inputreport', (event: any) => {
                const bytes = new Uint8Array(event.data.buffer);
                for (const byte of bytes) {
                    if (byte === 0x0D || byte === 0x0A) {
                        this.flushHidBuffer();
                    } else if (byte >= 0x20 && byte < 0x7F) {
                        this.hidBuffer += String.fromCharCode(byte);
                    }
                }
                // Timeout flush: some scanners omit the trailing Enter
                clearTimeout(this.hidFlushTimer);
                if (this.hidBuffer.length > 0) {
                    this.hidFlushTimer = setTimeout(() => this.flushHidBuffer(), 100);
                }
            });

            this.hidDevice.addEventListener('close', () => { this.hidDevice = null; });
            return true;
        } catch {
            this.hidDevice = null;
            return false;
        }
    }

    async disconnectScanner(): Promise<void> {
        if (this.hidDevice) {
            try { await this.hidDevice.close(); } catch { /* ignore */ }
            this.hidDevice = null;
        }
    }

    private flushHidBuffer(): void {
        clearTimeout(this.hidFlushTimer);
        const raw = this.hidBuffer.trim();
        this.hidBuffer = '';
        if (!raw) return;
        const barcode = this.parseBarcode(raw);
        if (barcode) this.ngZone.run(() => this.dispatchScan(barcode));
    }

    private onKeydown(event: KeyboardEvent): void {
        const url = this.router.url.split('?')[0];
        if (this.AUTH_URLS.includes(url)) return;

        console.log(`[Scanner] KEY key='${event.key}' len=${event.key.length} repeat=${event.repeat} target=${(event.target as HTMLElement).tagName}.${(event.target as HTMLElement).className?.slice(0,20)}`);

        if (event.key === 'Enter') {
            this.handleEnter(event);
            return;
        }

        // Escape always cancels
        if (event.key === 'Escape') {
            this.clearBuffer();
            return;
        }

        // F1-F12 pass through (fix: don't match letter 'F' in barcodes)
        if (/^F\d+$/.test(event.key) || event.key === 'Tab') {
            this.clearBuffer();
            return;
        }

        // Ignore modifier keys without resetting the buffer (essential for AZERTY layout & scanners emulating Shift/CapsLock)
        const MODIFIER_KEYS = ['Shift', 'CapsLock', 'Alt', 'Control', 'AltGraph', 'Meta', 'Unidentified'];
        if (MODIFIER_KEYS.includes(event.key)) {
            return;
        }

        // Non-printable keys (Enter, Escape, etc. handled earlier): ignore if not single character
        const isPrintable = event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey;
        if (!isPrintable) {
            if (!this.isScanSequence) this.clearBuffer();
            return;
        }

        const target = event.target as HTMLElement;
        const isField = this.isInputField(target);
        const now = Date.now();
        const interval = this.lastCharTime > 0 ? now - this.lastCharTime : 9999;
        this.lastCharTime = now;

        if (this.buffer.length === 0) {
            // First char — unknown yet if scan or manual; save snapshot and let through
            this.fieldSnapshot = isField ? (target as HTMLInputElement).value : null;
            this.fieldElement = isField ? (target as HTMLInputElement) : null;
            this.scannedSearchElement = (isField && this.isSearchInputField(target)) ? (target as HTMLInputElement) : null;
            this.buffer = event.key;
            this.isScanSequence = false;
            return;
        }

        if (interval <= this.SCAN_SPEED_MS) {
            // Fast enough for a scanner
            this.buffer += event.key;
            // Only declare a scan sequence after MIN_SCAN_CONFIRM_CHARS consecutive fast chars
            // to avoid false positives from fast human typing
            if (!this.isScanSequence && this.buffer.length >= this.MIN_SCAN_CONFIRM_CHARS) {
                this.isScanSequence = true;
                this.ngZone.run(() => this.restoreField());
            }
            if (this.isScanSequence) {
                event.preventDefault();
            } else if (this.buffer.length >= 2 && isField && !this.isSearchInputField(target)) {
                // Chars 2…(MIN_SCAN_CONFIRM_CHARS-1): scan not yet confirmed but interval is fast.
                // Prevent these chars from appearing in non-search inputs (quantity, price…)
                // so only char 1 can briefly leak before being snapped back on confirmation.
                // Search inputs are left alone — they are meant to receive the scanned value.
                event.preventDefault();
            }
        } else {
            // Too slow — manual typing; reset and treat this char as a new first char
            console.log(`[Scanner] SLOW char='${event.key}' interval=${interval}ms > ${this.SCAN_SPEED_MS}ms → buffer reset`);
            this.fieldSnapshot = isField ? (target as HTMLInputElement).value : null;
            this.fieldElement = isField ? (target as HTMLInputElement) : null;
            this.scannedSearchElement = (isField && this.isSearchInputField(target)) ? (target as HTMLInputElement) : null;
            this.buffer = event.key;
            this.isScanSequence = false;
        }
    }

    private handleEnter(event: KeyboardEvent): void {
        console.log(`[Scanner] Enter — isScan=${this.isScanSequence} bufferLen=${this.buffer.length} buffer='${this.buffer}'`);
        if (this.isScanSequence && this.buffer.length >= this.MIN_BARCODE_LENGTH) {
            event.preventDefault();
            const raw = this.buffer;
            this.clearBuffer();
            // Always emit via keyboard; dispatchScan deduplicates HID+keyboard overlap
            const barcode = this.parseBarcode(raw);
            console.log(`[Scanner] parsed='${barcode}'`);
            if (barcode) this.ngZone.run(() => this.dispatchScan(barcode));
        } else {
            this.clearBuffer();
        }
    }

    private dispatchScan(barcode: string): void {
        // Dedup: ignore the same barcode emitted twice within 600ms (HID + keyboard overlap)
        const now = Date.now();
        if (barcode === this.lastEmittedBarcode && now - this.lastEmitTime < 600) {
            console.log(`[Scanner] DEDUP — '${barcode}' ignored (already emitted ${now - this.lastEmitTime}ms ago)`);
            return;
        }
        this.lastEmittedBarcode = barcode;
        this.lastEmitTime = now;

        console.log(`[Scanner] dispatchScan barcode='${barcode}' url='${this.router.url}' dialogs=${this.matDialog.openDialogs.length} auth=${this.authService.isAuthenticated()}`);
        this.barcodeSubject.next(barcode);

        // Don't navigate while a dialog is open
        if (this.matDialog.openDialogs.length > 0) return;

        const url = this.router.url.split('?')[0];
        const activeEl = document.activeElement as HTMLElement;
        const searchTarget = this.scannedSearchElement || (this.isSearchInputField(activeEl) ? (activeEl as HTMLInputElement) : null);

        // On addOrder/updateOrder pages, barcodeSubject.next() (line above) already triggers processBarcode()
        // which performs an EXACT barcode match. Filling the search input here would also fire onInputChange
        // → debounceTime(300) → partial name/description search, which races against processBarcode and
        // shows wrong results. So we skip the searchTarget fill and let processBarcode handle it alone.
        const isOrderPage = url === '/addOrder' || url === '/updateOrder';
        if (!isOrderPage && searchTarget && searchTarget.ownerDocument.body.contains(searchTarget)) {
            console.log(`[Scanner] Search input targeted — setting value '${barcode}' and skipping redirection.`);
            searchTarget.value = barcode;
            searchTarget.dispatchEvent(new Event('input', { bubbles: true }));
            searchTarget.dispatchEvent(new Event('change', { bubbles: true }));
            try { searchTarget.focus(); } catch {}
            this.scannedSearchElement = null;
            return;
        }

        if (url !== '/addOrder' && url !== '/updateOrder') {
            this._pendingBarcode = barcode;
            console.log(`[Scanner] Navigating to /addOrder?clientId=1 with pendingBarcode='${barcode}'`);
            // Show loader immediately so the user sees feedback while /addOrder loads
            this.loaderService.show();
            this.ngZone.run(() => {
                this.router.navigate(['/addOrder'], { queryParams: { clientId: 1 } });
            });
        }
    }

    private isSearchInputField(el: HTMLElement | null): boolean {
        if (!el || el.tagName !== 'INPUT') return false;
        const input = el as HTMLInputElement;
        const isSearchType = input.type === 'search';
        const hasSearchClass = input.classList.contains('search-input') ||
                               !!input.closest('.search-box, .search-container, .search-wrap, .search-filters, .search-input-wrapper');
        const placeholder = (input.placeholder || '').toLowerCase();
        const isSearchPlaceholder = placeholder.includes('search') || placeholder.includes('rechercher');
        
        return isSearchType || hasSearchClass || isSearchPlaceholder;
    }

    /** Restore the field to the value it had before the first scan char leaked in. */
    private restoreField(): void {
        if (this.fieldElement) {
            const isSearch = this.isSearchInputField(this.fieldElement);
            if (!isSearch) {
                // Unfocus non-search inputs immediately so cursor doesn't blink
                this.fieldElement.blur();
            }
            if (this.fieldSnapshot !== null) {
                this.fieldElement.value = this.fieldSnapshot;
                // Dispatch input event silently to update NgModel binding without visual flicker
                this.fieldElement.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
    }

    private clearBuffer(): void {
        this.buffer = '';
        this.isScanSequence = false;
        this.fieldSnapshot = null;
        this.fieldElement = null;
        this.lastCharTime = 0;
    }

    private isInputField(el: HTMLElement): boolean {
        const tag = el.tagName;
        return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    }

    private parseBarcode(raw: string): string | null {
        if (!raw) return null;

        // Map French AZERTY unshifted number row characters to digits
        const azertyMap: { [key: string]: string } = {
            '&': '1', 'é': '2', '"': '3', "'": '4', '(': '5',
            '-': '6', 'è': '7', '_': '8', 'ç': '9', 'à': '0'
        };

        let converted = '';
        for (const char of raw) {
            converted += azertyMap[char] || char;
        }

        const numeric = converted.replace(/\D/g, '');
        if (numeric.length >= this.MIN_BARCODE_LENGTH) return numeric;
        if (converted.length >= this.MIN_BARCODE_LENGTH) return converted;
        return null;
    }
}
